import { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const SOCIAL_PROVIDERS = [
  {
    id: "google", name: "Google", label: "Continue with Google",
    bg: "bg-card border border-border hover:bg-white/5", text: "text-foreground/80",
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
    ),
  },
  {
    id: "microsoft", name: "Microsoft", label: "Continue with Microsoft",
    bg: "bg-card border border-border hover:bg-white/5", text: "text-foreground/80",
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24"><rect x="1" y="1" width="10" height="10" fill="#F25022"/><rect x="13" y="1" width="10" height="10" fill="#7FBA00"/><rect x="1" y="13" width="10" height="10" fill="#00A4EF"/><rect x="13" y="13" width="10" height="10" fill="#FFB900"/></svg>
    ),
  },
  {
    id: "meta", name: "Meta", label: "Continue with Meta",
    bg: "bg-[#1877F2] hover:bg-[#166FE5]", text: "text-white",
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="white"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
    ),
  },
  {
    id: "x", name: "X", label: "Continue with X",
    bg: "bg-black border border-white/15 hover:bg-gray-900", text: "text-white",
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="white"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
    ),
  },
];

export default function SocialLoginButtons() {
  const [providers, setProviders] = useState({});
  const [loading, setLoading] = useState("");

  useEffect(() => {
    api.get("/auth/providers/status").then((r) => setProviders(r.data)).catch(() => {});
  }, []);

  const handleSocialLogin = async (providerId) => {
    // Google uses Emergent's managed OAuth bridge
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (providerId === "google") {
      setLoading("google");
      const redirectUrl = window.location.origin + '/dashboard';
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      return;
    }

    const prov = providers[providerId];
    if (!prov?.configured) {
      toast.info(`${SOCIAL_PROVIDERS.find((p) => p.id === providerId)?.name} login is being configured. Please use email login for now.`);
      return;
    }
    setLoading(providerId);
    try {
      const r = await api.get(`/auth/${providerId}/login?purpose=login`);
      window.location.href = r.data.auth_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "OAuth login failed");
      setLoading("");
    }
  };

  return (
    <div className="space-y-2.5" data-testid="social-login-buttons">
      {SOCIAL_PROVIDERS.map((p) => {
        const configured = p.id === "google" ? true : providers[p.id]?.configured;
        return (
          <button
            key={p.id}
            onClick={() => handleSocialLogin(p.id)}
            disabled={loading === p.id}
            className={`w-full flex items-center justify-center gap-3 h-11 rounded-lg text-sm font-medium transition-all cursor-pointer border-0 ${p.bg} ${p.text} ${!configured ? "opacity-70" : ""}`}
            data-testid={`social-login-${p.id}`}
          >
            {loading === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : p.icon}
            <span>{p.label}</span>
          </button>
        );
      })}
    </div>
  );
}
